/**
 * Stock by depot — the logistics view.
 *
 * Narrowed on one line by company, location, point of sale and depot — the
 * stock structure, broadest first — and by what is available or reserved.
 * The selects cascade: a company offers only the places and depots it has,
 * and choosing a broader filter clears the narrower ones, so the line can
 * never describe a depot that is not there.
 *
 * Grouped the way the user chose. The levels — company, location, point of sale,
 * depot, and the article itself — nest in the order they pick, kept per user: « point de
 * vente puis dépôt », « article puis dépôt » to see where a reference sits, and so on.
 * The default is company → location → point of sale → depot → article.
 * Each line opens into the next level, read from the server only when it is opened, with
 * the page's own filters — one grouped query, whatever the order.
 *
 * It opens on the **exceptions**, not on everything. That is what the screen
 * is for: somebody looking at it wants to know what needs restocking, and a
 * page one of 32 086 rows sorted by reference answers a question nobody
 * asked. The backend has a partial index covering exactly those rows.
 *
 * The table shows what can be sold — available, real minus reserved — and
 * where. `reserved` is not editable anywhere: it is the sum of this portal's
 * own unshipped order lines, so a typed figure would put customer-facing
 * availability permanently out of step with the orders that produced it. The
 * "Réservé" filter finds the levels it holds.
 */

import { useCallback, useEffect, useMemo, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { quantity } from "@hardauto/core-i18n";
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  MultiSelect,
  Page,
  Toolbar,
  TreeTable,
  type TreeColumn,
  type TreeRow,
} from "@hardauto/core-ui";

import { ReferentialApi, type Site, type StockGroup, type StockGroupLevel } from "../api";
import { NAMESPACE } from "../i18n";
import type { ReferentialScope } from "../scope";
import { GroupingPicker } from "./GroupingPicker";
import { ALL_LEVELS, useGrouping, useLevelName } from "./grouping";
import "./referential.css";

/** What each filter has chosen: several values per level, which are alternatives. */
interface Chosen {
  companies: string[];
  cities: string[];
  points: string[];
  sites: string[];
}

/** A row of the page: a group of whichever level the user nested, or a note under one. */
interface StockNode {
  kind: StockGroupLevel | "note";
  name: string;
  /** A depot's code, or an article's reference. */
  code?: string;
  /** An article row: what the article is, beside its reference. */
  designation?: string;
  companies: string[];
  /** Distinct references held — counted at each level, not added up from below. */
  references: number;
  depots: number;
  available: number;
  reserved: number;
  /** The keys of the groups above it and its own: what opening it asks the server for. */
  path: string[];
}

/** What is known of a group's lines: loaded, or still being read. */
type Lines = StockGroup[] | "loading" | "failed";

/** This page's own saving slot; the default is every level, broadest first, article last. */
const LAYOUT_KEY = "stock_items";
const DEFAULT_LEVELS: readonly StockGroupLevel[] = ["company", "city", "point", "site", "article"];

/** The most lines one opened group lists; past it the filters narrow. */
const GROUP_LIMIT = 500;

interface Words {
  noLocation: string;
  noPoint: string;
  noCompany: string;
  loading: string;
  failed: string;
  truncated: string;
}

/** What names a group whose key is empty — no location, no point of sale, no company. */
function groupName(level: StockGroupLevel, group: StockGroup, words: Words): string {
  if (level === "article") return group.key;
  if (group.label) return group.label;
  return level === "city" ? words.noLocation : level === "point" ? words.noPoint : words.noCompany;
}

/** The groups of one level, each opening into the next level once that has been read. */
function stockRows(
  groups: readonly StockGroup[],
  levels: readonly StockGroupLevel[],
  path: readonly string[],
  lines: Record<string, Lines>,
  words: Words,
): TreeRow<StockNode>[] {
  const level = levels[path.length]!;
  const isLast = path.length + 1 >= levels.length;
  const note = (key: string, name: string): TreeRow<StockNode> => ({
    key: `${key}/note`,
    node: {
      kind: "note",
      name,
      companies: [],
      references: 0,
      depots: 0,
      available: 0,
      reserved: 0,
      path: [],
    },
  });
  const rows = groups.map((group): TreeRow<StockNode> => {
    const here = [...path, group.key];
    const key = linesKey(levels, here);
    const loaded = lines[key];
    const children: TreeRow<StockNode>[] | undefined = isLast
      ? undefined
      : loaded === undefined || loaded === "loading"
        ? [note(key, words.loading)]
        : loaded === "failed"
          ? [note(key, words.failed)]
          : stockRows(loaded, levels, here, lines, words);
    return {
      key,
      node: {
        kind: level,
        name: groupName(level, group, words),
        ...(level === "site" || level === "article" ? { code: group.key } : {}),
        ...(level === "article" ? { designation: group.label } : {}),
        companies: group.companies,
        references: group.references,
        depots: group.depots,
        available: Number(group.available),
        reserved: Number(group.reserved),
        path: here,
      },
      ...(children ? { children } : {}),
    };
  });
  return groups.length >= GROUP_LIMIT
    ? [...rows, note(linesKey(levels, path), words.truncated)]
    : rows;
}

/** One group's place in the cache and in the table: the levels and keys down to it. */
function linesKey(levels: readonly StockGroupLevel[], path: readonly string[]): string {
  return path.map((key, depth) => `${levels[depth]}:${encodeURIComponent(key)}`).join("/");
}

/** What a category filter injected by the application is given: the chosen path, and a way to change it. */
export interface CategoryFilterProps {
  /** The category's path in the web tree; empty for the whole catalogue. */
  selected: string;
  onSelect: (path: string) => void;
}

export function StockScreen({
  client,
  scope,
  CategoryFilter,
}: {
  client: ApiClient;
  /** The working-context bar's company and depot, when one is injected. */
  scope?: ReferentialScope;
  /**
   * The web catalogue's tree — activité, famille, sous-famille, segment — to narrow the stock
   * to a category. The catalogue's own feature, so the application injects it; without it
   * there is no category filter.
   */
  CategoryFilter?: ComponentType<CategoryFilterProps>;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const levelName = useLevelName();
  const grouping = useGrouping(api, LAYOUT_KEY, DEFAULT_LEVELS, ALL_LEVELS);
  const levelsKey = grouping.levels.join(">");

  const [term, setTerm] = useState("");
  // Seeded from the working context, once: the screen opens already
  // narrowed to it, but the selects stay free — a reader who wants to see
  // another depot must not find them locked to this one.
  const [chosen, setChosen] = useState<Chosen>(() => ({
    companies: scope?.companyCode ? [scope.companyCode] : [],
    cities: [],
    points: [],
    sites: scope?.siteCode ? [scope.siteCode] : [],
  }));
  const [availableOnly, setAvailableOnly] = useState(false);
  const [reservedOnly, setReservedOnly] = useState(false);
  // Off by default: the screen opens on everything, and the exceptions are one tick away.
  const [alertsOnly, setAlertsOnly] = useState(false);
  const [categoryPath, setCategoryPath] = useState("");
  // Shown or hidden as the catalogue list does it, and remembered the same way, per browser.
  const [showTree, setShowTree] = useState(readTreePreference);

  const search = useDebounced(term, 250);
  // The wording every multi-value filter of this screen shares.
  const multi = {
    countLabel: (count: number) => t("stock.selectedCount", { count }),
    clearLabel: t("stock.clearFilter"),
    searchLabel: t("stock.searchOptions"),
  };

  const sitesFetcher = useCallback(() => api.listSites(true, false), [api]);
  const sites = useResource(sitesFetcher);
  const companiesFetcher = useCallback(() => api.listCompanies(true, false), [api]);
  const companies = useResource(companiesFetcher);
  const pointsFetcher = useCallback(() => api.listPointsOfSale(true, false), [api]);
  const points = useResource(pointsFetcher);

  // What each select offers, given the broader ones. A depot's location is its
  // place's city, or its own when it has no place — as on the depot list.
  // A depot of a hidden company is still active itself, but the company is
  // not offered: its depots are not either. Unattached depots stay.
  const activeCompanies = companies.data
    ? new Set(companies.data.items.map((entry) => entry.code))
    : null;
  const allSites = (sites.data?.items ?? []).filter(
    (entry) => !activeCompanies || !entry.company_code || activeCompanies.has(entry.company_code),
  );
  const allPoints = points.data?.items ?? [];
  const pointsByCode = new Map(allPoints.map((entry) => [entry.code, entry]));
  const locationOf = (entry: Site) =>
    pointsByCode.get(entry.point_of_sale_code)?.city || entry.city;
  /** `all` when nothing is chosen at a level, otherwise any of the chosen. */
  const pass = (picked: readonly string[], value: string) =>
    picked.length === 0 || picked.includes(value);
  const inCompany = allSites.filter((entry) => pass(chosen.companies, entry.company_code));
  const cities = [...new Set(inCompany.map(locationOf).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, i18n.language),
  );
  const inCity = inCompany.filter((entry) => pass(chosen.cities, locationOf(entry)));
  const offeredPoints = allPoints.filter(
    (entry) =>
      (chosen.companies.length === 0 && chosen.cities.length === 0) ||
      inCity.some((depot) => depot.point_of_sale_code === entry.code),
  );
  const offeredSites = inCity.filter((entry) => pass(chosen.points, entry.point_of_sale_code));

  /**
   * Change some levels, dropping the narrower choices the wider ones no longer
   * allow. Ticking a company must not leave a depot of another one chosen
   * behind it — the list would be empty and the reader would not see why. Not
   * done until the structure has loaded, or everything would look invalid.
   */
  const choose = (patch: Partial<Chosen>) => {
    setChosen((current) => {
      const next = { ...current, ...patch };
      if (!sites.data || !points.data) return next;
      const fitCompany = allSites.filter((entry) => pass(next.companies, entry.company_code));
      const cityOptions = new Set(fitCompany.map(locationOf));
      const nextCities = next.cities.filter((city) => cityOptions.has(city));
      const fitCity = fitCompany.filter((entry) => pass(nextCities, locationOf(entry)));
      const pointOptions = new Set(fitCity.map((entry) => entry.point_of_sale_code));
      const nextPoints = next.points.filter((code) => pointOptions.has(code));
      const siteOptions = new Set(
        fitCity
          .filter((entry) => pass(nextPoints, entry.point_of_sale_code))
          .map((entry) => entry.code),
      );
      return {
        companies: next.companies,
        cities: nextCities,
        points: nextPoints,
        sites: next.sites.filter((code) => siteOptions.has(code)),
      };
    });
  };

  // The context's Actif / Non actif, as the stock query names it.
  const activeFilter = useMemo<{ article_active?: "active" | "inactive" }>(
    () => (scope?.active && scope.active !== "all" ? { article_active: scope.active } : {}),
    [scope?.active],
  );

  // The page's own filters, as every grouped query carries them.
  const filters = useMemo(
    () => ({
      ...(search ? { search } : {}),
      site_codes: chosen.sites,
      company_codes: chosen.companies,
      point_of_sale_codes: chosen.points,
      cities: chosen.cities,
      // What the back office hid is not shown here, however it is reached.
      include_hidden: false,
      ...(availableOnly ? { available_only: true } : {}),
      ...(reservedOnly ? { reserved_only: true } : {}),
      ...(alertsOnly ? { below_threshold_only: true } : {}),
      ...activeFilter,
      ...(categoryPath ? { category_path: categoryPath } : {}),
      limit: GROUP_LIMIT,
    }),
    [search, chosen, availableOnly, reservedOnly, alertsOnly, activeFilter, categoryPath],
  );

  // The first level, tagged with the arrangement it was read for: a new arrangement must
  // never be drawn with the old arrangement's lines while its own are on the way.
  const fetcher = useCallback(
    () =>
      grouping.ready
        ? api
            .stockGroups(grouping.levels, [], filters)
            .then((page) => ({ levelsKey, groups: page.items }))
        : Promise.resolve(null),
    // `grouping.levels` is `levelsKey`, spelt out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, filters, levelsKey, grouping.ready],
  );
  const levels = useResource(fetcher);

  // The lines of the groups opened so far. Read one group at a time, with the page's own
  // filters, and forgotten when the filters or the arrangement change.
  const [lines, setLines] = useState<Record<string, Lines>>({});
  useEffect(() => {
    setLines({});
  }, [levels.data]);

  function loadLines(node: StockNode) {
    const key = linesKey(grouping.levels, node.path);
    if (lines[key] !== undefined) return;
    setLines((current) => ({ ...current, [key]: "loading" }));
    api
      .stockGroups(grouping.levels, node.path, filters)
      .then((page) => setLines((current) => ({ ...current, [key]: page.items })))
      .catch(() => setLines((current) => ({ ...current, [key]: "failed" })));
  }

  const words: Words = {
    noLocation: t("stockByDepot.noLocation"),
    noPoint: t("stockByDepot.noPoint"),
    noCompany: t("stockByDepot.noCompany"),
    loading: t("stock.items.loading"),
    failed: t("stock.items.failed"),
    truncated: t("stock.groups.truncated", { count: GROUP_LIMIT }),
  };
  const current = levels.data?.levelsKey === levelsKey ? levels.data.groups : null;
  const rows = useMemo(
    () => (current ? stockRows(current, grouping.levels, [], lines, words) : []),
    // `words` is rebuilt each render from `t`, which only changes with the language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current, lines, i18n.language, levelsKey],
  );
  const qty = (value: number) => quantity(value.toFixed(3), i18n.language);

  // Where it is, how many references it holds, then how much can be sold.
  const columns: TreeColumn<StockNode>[] = [
    {
      key: "place",
      header: grouping.levels.map(levelName).join(" / "),
      render: (node) =>
        node.kind === "site" ? (
          <span>
            {node.name} <span className="hardauto-cell__muted">{node.code}</span>
          </span>
        ) : node.kind === "article" ? (
          <span>
            <span className="hardauto-ref">{node.name}</span>{" "}
            <span className="hardauto-cell__muted">{node.designation}</span>
          </span>
        ) : node.kind === "note" ? (
          <span className="hardauto-cell__muted">{node.name}</span>
        ) : (
          node.name
        ),
    },
    {
      key: "company",
      header: t("stock.column.company"),
      render: (node) => node.companies.join(", ") || "—",
    },
    {
      key: "references",
      header: t("stock.column.references"),
      align: "end",
      render: (node) =>
        node.kind === "article" || node.kind === "note" ? null : (
          <span className="hardauto-num hardauto-num--strong">{node.references}</span>
        ),
    },
    {
      key: "depots",
      header: t("stockByDepot.column.depots"),
      align: "end",
      render: (node) =>
        node.kind === "site" || node.kind === "note" ? null : (
          <span className="hardauto-num">{node.depots}</span>
        ),
    },
    {
      key: "available",
      header: t("stock.column.available"),
      align: "end",
      render: (node) =>
        node.kind === "note" ? null : <span className="hardauto-num">{qty(node.available)}</span>,
    },
    {
      key: "reserved",
      header: t("stock.column.reserved"),
      align: "end",
      render: (node) =>
        node.kind === "note" ? null : <span className="hardauto-num">{qty(node.reserved)}</span>,
    },
  ];

  const isNarrowed = Boolean(
    search ||
    categoryPath ||
    chosen.sites.length ||
    chosen.companies.length ||
    chosen.points.length ||
    chosen.cities.length ||
    availableOnly ||
    reservedOnly ||
    alertsOnly,
  );

  return (
    <Page title={t("stock.title")} description={t("stock.intro")}>
      <Toolbar>
        <div className="hardauto-search">
          <Input
            type="search"
            aria-label={t("stock.searchLabel")}
            placeholder={t("stock.searchPlaceholder")}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>
        {CategoryFilter ? (
          <Button
            variant="secondary"
            aria-expanded={showTree}
            onClick={() =>
              setShowTree((open) => {
                writeTreePreference(!open);
                return !open;
              })
            }
          >
            {showTree ? t("stock.hideTree") : t("stock.showTree")}
          </Button>
        ) : null}
      </Toolbar>

      <div className="hardauto-stock-filters" role="group" aria-label={t("stock.filtersLabel")}>
        <MultiSelect
          label={t("stock.companyLabel")}
          allLabel={t("stock.allCompanies")}
          options={(companies.data?.items ?? []).map((entry) => ({
            value: entry.code,
            label: entry.name,
          }))}
          selected={chosen.companies}
          onChange={(next) => choose({ companies: next })}
          {...multi}
        />
        <MultiSelect
          label={t("stock.cityLabel")}
          allLabel={t("stock.allCities")}
          options={cities.map((entry) => ({ value: entry, label: entry }))}
          selected={chosen.cities}
          onChange={(next) => choose({ cities: next })}
          {...multi}
        />
        <MultiSelect
          label={t("stock.pointLabel")}
          allLabel={t("stock.allPoints")}
          options={offeredPoints.map((entry) => ({
            value: entry.code,
            label: entry.city ? `${entry.name} (${entry.city})` : entry.name,
          }))}
          selected={chosen.points}
          onChange={(next) => choose({ points: next })}
          {...multi}
        />
        <MultiSelect
          label={t("stock.siteLabel")}
          allLabel={t("stock.allSites")}
          options={offeredSites.map((entry) => ({ value: entry.code, label: entry.name }))}
          selected={chosen.sites}
          onChange={(next) => choose({ sites: next })}
          {...multi}
        />
        <Checkbox
          label={t("stock.onlyAvailable")}
          checked={availableOnly}
          onChange={(event) => setAvailableOnly(event.target.checked)}
        />
        <Checkbox
          label={t("stock.onlyReserved")}
          checked={reservedOnly}
          onChange={(event) => setReservedOnly(event.target.checked)}
        />
        <Checkbox
          label={t("stock.onlyAlerts")}
          checked={alertsOnly}
          onChange={(event) => setAlertsOnly(event.target.checked)}
        />
      </div>
      <GroupingPicker grouping={grouping} allowed={ALL_LEVELS} />

      <div className={CategoryFilter && showTree ? "hardauto-stock-layout" : undefined}>
        {CategoryFilter && showTree ? (
          <CategoryFilter selected={categoryPath} onSelect={setCategoryPath} />
        ) : null}
        <div className="hardauto-stock-layout__results">
          {levels.failed ? (
            <ErrorState
              title={t("error.title")}
              onRetry={levels.reload}
              retryLabel={t("error.retry")}
            />
          ) : !grouping.ready || !current ? (
            <LoadingState label={t("stock.loading")} />
          ) : rows.length === 0 ? (
            isNarrowed ? (
              <EmptyState
                title={t("stock.filtered.title")}
                description={t("stock.filtered.body")}
              />
            ) : (
              <EmptyState title={t("stock.empty.title")} description={t("stock.empty.body")} />
            )
          ) : (
            <TreeTable<StockNode>
              // A new arrangement starts closed: the open lines belonged to the old one.
              key={levelsKey}
              caption={t("stock.caption")}
              columns={columns}
              rows={rows}
              onToggle={(row, open) => {
                if (open && row.node.kind !== "note" && row.children?.length) loadLines(row.node);
              }}
              toggleLabel={(node, open) =>
                `${open ? t("stockByDepot.collapse") : t("stockByDepot.expand")} ${node.name}`
              }
            />
          )}
        </div>
      </div>
    </Page>
  );
}

/** Whether the category tree is shown; its own key, so the catalogue list's choice stays its own. */
const TREE_KEY = "hardauto.stock.tree";

function readTreePreference(): boolean {
  try {
    return window.localStorage.getItem(TREE_KEY) !== "hidden";
  } catch {
    return true;
  }
}

function writeTreePreference(visible: boolean): void {
  try {
    window.localStorage.setItem(TREE_KEY, visible ? "shown" : "hidden");
  } catch {
    // Private window or blocked storage: the choice just lasts for this visit.
  }
}
